import { useMemo, useState } from 'react';
import { getReferenceString } from '@medplum/core';
import { Appointment } from '@medplum/fhirtypes';
import { TimelineItemWithIcon } from './TimelineItems/TimelineItemWithIcon';
import { UnstyledButton, Collapse, Group, Stack, Text } from '@mantine/core';
import { AppointmentTimelineItem } from './TimelineItems/AppointmentTimelineItem';
import { AppointmentTimelineGroup, TimelineAppointment, TimelineItemType } from './types';
import { useAppointmentTypesForScheduling } from '@/pages/PatientTimelinePage/hooks/useAppointmentTypesForScheduling';

interface FutureAppointmentsProps {
  futureAppointments: TimelineAppointment[];
  onAppointmentStatusChange?: (updatedAppointment: Appointment) => void;
}

export function FutureAppointments({ futureAppointments, onAppointmentStatusChange }: FutureAppointmentsProps) {
  const [isMainCollapseOpen, setMainCollapseOpen] = useState(false);
  const [groupCollapseOpen, setGroupCollapseOpen] = useState(new Set<string>());
  const { activityCodeSystem } = useAppointmentTypesForScheduling();

  const toggleGroupCollapse = (groupId: string) => {
    setGroupCollapseOpen((prev) => {
      const newSet = new Set(prev);
      if (prev.has(groupId)) {
        newSet.delete(groupId);
      } else {
        newSet.add(groupId);
      }
      return newSet;
    });
  };

  const futureAppointmentGroups = useMemo<AppointmentTimelineGroup[]>(
    () =>
      [...futureAppointments]
        .sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime())
        .map((appointment) => ({
          type: TimelineItemType.APPOINTMENT,
          id: `future-appointment-${getReferenceString(appointment.reference)}`,
          timestamp: appointment.timestamp,
          items: [appointment],
        })),
    [futureAppointments]
  );

  if (futureAppointments.length === 0) {
    return null;
  }

  // Cancelled appointments are listed but not counted as upcoming.
  const cancelledCount = futureAppointments.filter(({ appointment }) => appointment.status === 'cancelled').length;
  const activeCount = futureAppointments.length - cancelledCount;

  return (
    <Stack px="md" pb="md">
      <TimelineItemWithIcon isLast={false} group={futureAppointmentGroups[0]}>
        <UnstyledButton w="100%" onClick={() => setMainCollapseOpen(!isMainCollapseOpen)} pt="0.4rem" mb="sm">
          <Group justify="space-between" align="center">
            <Text size="lg">
              {activeCount} future appointment{activeCount === 1 ? '' : 's'}
              {cancelledCount > 0 && ` · ${cancelledCount} cancelled`}
            </Text>
            <Text size="md" fw={700} c="blue.7">
              {isMainCollapseOpen ? 'Show less...' : 'Show more...'}
            </Text>
          </Group>
        </UnstyledButton>
      </TimelineItemWithIcon>

      <Collapse in={isMainCollapseOpen}>
        <Stack gap="sm" style={{ overflow: 'hidden' }} pl="1px" ml="-1px">
          {futureAppointmentGroups.map((group, index) => (
            <TimelineItemWithIcon key={group.id} group={group} isLast={index === futureAppointmentGroups.length - 1}>
              <AppointmentTimelineItem
                group={group}
                activityCodeSystem={activityCodeSystem ?? []}
                collapsed={!groupCollapseOpen.has(group.id)}
                onCollapseToggle={toggleGroupCollapse}
                onStatusChange={onAppointmentStatusChange}
              />
            </TimelineItemWithIcon>
          ))}
        </Stack>
      </Collapse>
    </Stack>
  );
}
